import {IEditor, EditContext, RectProps} from '@visactor/vtable-editors';

export interface FileEditorConfig {
  maxSize?: number; // 最大文件大小(MB)
  acceptTypes?: string[]; // 支持的文件类型
  multiple?: boolean; // 是否支持多选
  uploadUrl?: string; // 上传接口地址
  onUpload?: (file: File) => Promise<{url: string; name: string; size: number}>; // 自定义上传函数
}

export class FileEditor implements IEditor {
  editorConfig: FileEditorConfig;
  element: HTMLDivElement;
  container: HTMLElement;
  successCallback: Function;
  fileInput: HTMLInputElement;
  currentValue: Array<{url: string; name: string; size: number}>;

  constructor(editorConfig: FileEditorConfig = {}) {
    this.editorConfig = {
      maxSize: 50, // 默认50MB
      acceptTypes: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'image/*'
      ],
      multiple: true,
      ...editorConfig
    };
  }

  onStart({container, value, referencePosition, endEdit}: EditContext) {
    this.container = container;
    this.successCallback = endEdit;
    this.currentValue = value || [];

    // 创建编辑器容器
    const editorDiv = document.createElement('div');
    editorDiv.className = 'vtable-file-editor';
    editorDiv.style.cssText = `
      position: absolute;
      background: white;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      padding: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      min-width: 350px;
      max-width: 450px;
      z-index: 1000;
      top: 0;
      left: 0;
    `;

    // 创建上传区域
    const uploadArea = document.createElement('div');
    uploadArea.className = 'upload-area';
    uploadArea.style.cssText = `
      border: 2px dashed #d9d9d9;
      border-radius: 4px;
      padding: 20px;
      text-align: center;
      cursor: pointer;
      transition: border-color 0.3s;
      margin-bottom: 8px;
    `;
    uploadArea.innerHTML = `
      <div style="color: #666; font-size: 14px;">
        <div>📁 点击或拖拽文件到此处</div>
        <div style="font-size: 12px; margin-top: 4px;">
          支持 PDF、Word、Excel、图片等格式
        </div>
      </div>
    `;

    // 创建隐藏的文件输入
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = this.editorConfig.acceptTypes?.join(',') || '*/*';
    this.fileInput.multiple = this.editorConfig.multiple || false;
    this.fileInput.style.display = 'none';

    // 创建文件列表区域
    const fileListArea = document.createElement('div');
    fileListArea.className = 'file-list-area';
    fileListArea.style.cssText = `
      max-height: 250px;
      overflow-y: auto;
      margin-bottom: 8px;
    `;

    // 创建按钮区域
    const buttonArea = document.createElement('div');
    buttonArea.style.cssText = `
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    `;

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = '取消';
    cancelBtn.style.cssText = `
      padding: 4px 12px;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      background: white;
      cursor: pointer;
    `;

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = '确定';
    confirmBtn.style.cssText = `
      padding: 4px 12px;
      border: 1px solid #1890ff;
      border-radius: 4px;
      background: #1890ff;
      color: white;
      cursor: pointer;
    `;

    buttonArea.appendChild(cancelBtn);
    buttonArea.appendChild(confirmBtn);

    editorDiv.appendChild(uploadArea);
    editorDiv.appendChild(fileListArea);
    editorDiv.appendChild(buttonArea);
    editorDiv.appendChild(this.fileInput);

    this.element = editorDiv;
    container.appendChild(editorDiv);

    // 事件绑定
    this.bindEvents(uploadArea, fileListArea, cancelBtn, confirmBtn);

    // 显示现有文件
    this.renderFileList(fileListArea);

    if (referencePosition?.rect) {
      this.adjustPosition(referencePosition.rect);
    }
  }

  private bindEvents(
    uploadArea: HTMLElement,
    fileListArea: HTMLElement,
    cancelBtn: HTMLElement,
    confirmBtn: HTMLElement
  ) {
    // 点击上传区域
    uploadArea.addEventListener('click', () => {
      this.fileInput.click();
    });

    // 拖拽事件
    uploadArea.addEventListener('dragover', (e) => {
      e.preventDefault();
      uploadArea.style.borderColor = '#1890ff';
    });

    uploadArea.addEventListener('dragleave', () => {
      uploadArea.style.borderColor = '#d9d9d9';
    });

    uploadArea.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadArea.style.borderColor = '#d9d9d9';
      const files = Array.from(e.dataTransfer?.files || []) as File[];
      this.handleFiles(files, fileListArea);
    });

    // 文件选择
    this.fileInput.addEventListener('change', (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      this.handleFiles(files, fileListArea);
    });

    // 按钮事件
    cancelBtn.addEventListener('click', () => {
      this.successCallback(false);
    });

    confirmBtn.addEventListener('click', () => {
      this.successCallback(true);
    });
  }

  private async handleFiles(files: File[], fileListArea: HTMLElement) {
    for (const file of files) {
      if (!this.validateFile(file)) continue;

      try {
        const fileInfo = await this.uploadFile(file);
        this.currentValue.push(fileInfo);
        this.renderFileList(fileListArea);
      } catch (error) {
        console.error('上传失败:', error);
        // 可以显示错误提示
      }
    }
  }

  private validateFile(file: File): boolean {
    const maxSize = (this.editorConfig.maxSize || 50) * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`文件大小不能超过 ${this.editorConfig.maxSize}MB`);
      return false;
    }

    return true;
  }

  private async uploadFile(file: File): Promise<{url: string; name: string; size: number}> {
    if (this.editorConfig.onUpload) {
      return await this.editorConfig.onUpload(file);
    }

    // 默认创建本地URL
    return {
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size
    };
  }

  private getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const iconMap: Record<string, string> = {
      'pdf': '📄',
      'doc': '📝',
      'docx': '📝',
      'xls': '📊',
      'xlsx': '📊',
      'ppt': '📋',
      'pptx': '📋',
      'txt': '📄',
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
      'gif': '🖼️',
      'zip': '🗂️',
      'rar': '🗂️'
    };
    return iconMap[ext || ''] || '📎';
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  private renderFileList(fileListArea: HTMLElement) {
    fileListArea.innerHTML = '';
    
    this.currentValue.forEach((fileInfo, index) => {
      const fileItem = document.createElement('div');
      fileItem.style.cssText = `
        display: flex;
        align-items: center;
        padding: 8px;
        margin-bottom: 4px;
        border: 1px solid #f0f0f0;
        border-radius: 4px;
        background: #fafafa;
      `;

      const fileIcon = document.createElement('span');
      fileIcon.textContent = this.getFileIcon(fileInfo.name);
      fileIcon.style.cssText = `
        font-size: 20px;
        margin-right: 8px;
      `;

      const fileDetails = document.createElement('div');
      fileDetails.style.cssText = `
        flex: 1;
        overflow: hidden;
      `;

      const fileName = document.createElement('div');
      fileName.textContent = fileInfo.name;
      fileName.style.cssText = `
        font-size: 14px;
        font-weight: 500;
        color: #333;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 2px;
      `;

      const fileSize = document.createElement('div');
      fileSize.textContent = this.formatFileSize(fileInfo.size);
      fileSize.style.cssText = `
        font-size: 12px;
        color: #666;
      `;

      const downloadBtn = document.createElement('button');
      downloadBtn.textContent = '下载';
      downloadBtn.style.cssText = `
        padding: 2px 8px;
        border: 1px solid #1890ff;
        border-radius: 4px;
        background: white;
        color: #1890ff;
        cursor: pointer;
        font-size: 12px;
        margin-right: 4px;
      `;

      const deleteBtn = document.createElement('button');
      deleteBtn.textContent = '删除';
      deleteBtn.style.cssText = `
        padding: 2px 8px;
        border: 1px solid #ff4d4f;
        border-radius: 4px;
        background: white;
        color: #ff4d4f;
        cursor: pointer;
        font-size: 12px;
      `;

      downloadBtn.addEventListener('click', () => {
        const a = document.createElement('a');
        a.href = fileInfo.url;
        a.download = fileInfo.name;
        a.click();
      });

      deleteBtn.addEventListener('click', () => {
        this.currentValue.splice(index, 1);
        this.renderFileList(fileListArea);
      });

      fileDetails.appendChild(fileName);
      fileDetails.appendChild(fileSize);

      fileItem.appendChild(fileIcon);
      fileItem.appendChild(fileDetails);
      fileItem.appendChild(downloadBtn);
      fileItem.appendChild(deleteBtn);
      fileListArea.appendChild(fileItem);
    });
  }

  adjustPosition(rect: RectProps) {
    this.element.style.top = rect.top + 'px';
    this.element.style.left = rect.left + 'px';
  }

  getValue() {
    return this.currentValue;
  }

  onEnd() {
    if (this.element && this.container.contains(this.element)) {
      this.container.removeChild(this.element);
    }
  }

  isEditorElement(target: HTMLElement): boolean {
    return this.element && this.element.contains(target);
  }
}